import { LitElement, unsafeCSS, html, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import closeIcon from '~/assets/icons/v2-close.svg?raw';
import { uploadFileToS3, fetchDirectUploadMetadata, generateFileUploadError } from '~/shared/modules/file-upload';
import { onNextRepaint } from '~/shared/utils/animation';
import { DrbAttachment } from '~/web-components/drb-attachment/drb-attachment';
import styles from './drb-s3-upload.scss?inline';

@customElement('drb-s3-upload')
class DrbS3Upload extends LitElement {
  static styles = unsafeCSS(styles);
  private _cancelTokenSource = null;

  @property({ attribute: false, type: Object })
  file: File | null = null;

  @property({ attribute: false, type: String })
  s3Kind: 'default' | 'engagement_attachment' | 'avatar' = 'default';

  @property({ attribute: 'hide-cancel', type: Boolean })
  hideCancel: false;

  @state()
  s3FileDetailsId = '';

  @state()
  s3FullUrl = '';

  @state()
  status: 'UPLOADING' | 'CANCELED' | 'COMPLETE' | 'ERROR' = 'UPLOADING';

  async connectedCallback() {
    super.connectedCallback();
    if (!this.file) return;

    let maxFileSize;

    this._setProgress(0);

    try {
      const uploadMetaData = await fetchDirectUploadMetadata(false, this.s3Kind);
      maxFileSize = uploadMetaData?.maxFileSize;

      await uploadFileToS3({
        uploadMetaData,
        maxFileSize,
        file: this.file,
        kind: this.s3Kind,

        onInit: ({ uploadCancelTokenSource }) => {
          this._cancelTokenSource = uploadCancelTokenSource;
        },

        onProgress: ({ progressPercent }) => {
          this._setProgress(progressPercent);
        },

        onSuccess: ({ file, s3FileDetailsId, s3FullUrl }) => {
          this.status = 'COMPLETE';
          this.s3FileDetailsId = s3FileDetailsId;
          this.s3FullUrl = s3FullUrl;

          this.dispatchEvent(new CustomEvent('drb-s3-upload:file-uploaded', {
            detail: {
              file,
              s3FileDetailsId,
              s3FullUrl
            },
            bubbles: true,
            composed: true
          }));
        },
      });
    } catch (error) {
      this.status = 'ERROR';
      this._setProgress(100);

      generateFileUploadError({
        context: 'media',
        error,
        fileName: this.file.name,
        maxFileSize
      });

      this.dispatchEvent(new CustomEvent('drb-s3-upload:error', {
        detail: { error },
        bubbles: true,
        composed: true
      }));

      this.querySelectorAll('drb-attachment').forEach((attachment: DrbAttachment) => {
        attachment.error = true;
      });
    }
  }

  private _setProgress(progressPercent: number) {
    this.style.setProperty('--upload-progress', `${progressPercent}%`);
  }

  cancelUpload() {
    this.status = 'CANCELED';

    if (this._cancelTokenSource) {
      this._cancelTokenSource.cancel('Upload canceled by user');
    }

    this.dispatchEvent(new CustomEvent('drb-s3-upload:canceled', {
      bubbles: true,
      composed: true
    }));

    onNextRepaint(() => {
      this.remove();
    });
  }

  protected willUpdate(changedProperties: PropertyValues<this>): void {
    if (changedProperties.has('status')) {
      this.dispatchEvent(new CustomEvent('drb-s3-upload:status-changed', {
        detail: { status: this.status },
        bubbles: true,
        composed: true
      }));
    }
  }

  render() {
    return html`
      <slot></slot>

      <button
        type="button"
        class="cancel-btn"
        ?hidden="${this.hideCancel}"
        @click="${this.cancelUpload}"
      >
        ${unsafeHTML(closeIcon)}
      </button>
    `;
  }
}

export { DrbS3Upload };
